import React, { ReactNode } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { PifDoneRow, PifDoneStep, PifDoneTone } from "../done/PifDoneScreen";
import { APPROVAL_ROLES } from "../../../../services/pifService";
import { PifApprovalOutcome, PifApproverRole } from "../types";

interface ApprovalDoneContent {
  icon: ReactNode;
  tone: PifDoneTone;
  title: string;
  lede: string;
  receipt: PifDoneRow[];
  nextSteps: PifDoneStep[];
}

const OUTCOME_ICON: Record<PifApprovalOutcome, ReactNode> = {
  signed: <CheckIcon />,
  declined: <CloseIcon />,
  changes: <UndoIcon />,
};

const OUTCOME_TONE: Record<PifApprovalOutcome, PifDoneTone> = {
  signed: "success",
  declined: "warning",
  changes: "info",
};

export const buildApprovalDoneContent = (
  pifId: string,
  role: PifApproverRole,
  outcome: PifApprovalOutcome,
  signedAt: string,
): ApprovalDoneContent => {
  const roleMeta = APPROVAL_ROLES[role];
  const isFinal = outcome === "signed" && role === "exec";

  const title = outcome === "signed" ? (isFinal ? "The hire has been approved." : "Signed.") : outcome === "declined" ? "Declined." : "Sent back.";
  const lede =
    outcome === "signed"
      ? isFinal
        ? "All four signatures are in and the record is locked."
        : `Now with the ${roleMeta.forward}.`
      : outcome === "declined"
        ? "The request is closed and the supervisor has been told."
        : "The supervisor can correct the form and resubmit it.";

  const receipt: PifDoneRow[] =
    outcome === "signed"
      ? [
          { k: "Reference", v: pifId },
          { k: "Action", v: isFinal ? "Final approval" : "Signed and forwarded" },
          { k: "Signed by", v: roleMeta.label },
          { k: "Timestamp", v: signedAt },
          { k: "Next", v: roleMeta.receiptNext },
        ]
      : [
          { k: "Reference", v: pifId },
          { k: "Action", v: outcome === "declined" ? "Declined" : "Sent back for changes" },
          { k: "By", v: roleMeta.label },
          { k: "Timestamp", v: signedAt },
        ];

  const nextSteps: PifDoneStep[] =
    outcome === "signed"
      ? isFinal
        ? [
            { title: "The hire has been approved", body: "All four signatures are in. The record is locked." },
            { title: "Onboarding opens", body: "HR creates the payroll and systems record." },
            { title: "The supervisor gets a copy", body: "The fully signed PDF is sent today." },
          ]
        : [
            { title: "Your signature is recorded", body: "Bound to this version of the form." },
            { title: `${roleMeta.forward} is notified`, body: "A signing link goes out within a minute." },
            { title: "You'll get the final copy", body: "Once the last signature is in, everyone receives the signed PDF." },
          ]
      : outcome === "declined"
        ? [
            { title: "The request is closed", body: "Nobody further in the chain is asked to sign." },
            { title: "The supervisor is told", body: "They get your reason and note in full." },
            { title: "Nothing reaches payroll", body: "No record is created for this hire." },
          ]
        : [
            { title: "Sent back to the supervisor", body: "They can edit and resubmit." },
            { title: "Signatures reset", body: "Any signature already given on this version is voided." },
            { title: "It comes back to you", body: "You'll get a fresh link once it's resubmitted." },
          ];

  return { icon: OUTCOME_ICON[outcome], tone: OUTCOME_TONE[outcome], title, lede, receipt, nextSteps };
};
